/**
 * One row per TA action board — which is one row per recruiter, because a board has one owner.
 *
 * ── What this is for, stated on the panel itself ─────────────────────────────────────────────
 * The team committed to this tool, so the question is where support is needed: which parts of the
 * board somebody has not been shown yet. That is why the checklist under each row leads with what
 * has NOT been used — a list of what somebody did is a report about them, a list of what they have
 * never tried is a list of things to sit down and show them.
 *
 * ── Why naming people is acceptable here and nowhere else on this board ──────────────────────
 * Everything else on this page is deliberately anonymous. A board has exactly one owner, so
 * "usage of Anna's board" and "what Anna did" are the same sentence whichever way the column is
 * labelled, and anonymising it would have been a naming trick rather than a control. What bounds
 * it instead is scope, enforced server-side: the boards only, nothing else in People Space.
 */
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@finn-auto/design-system/atoms/accordion';
import { Tooltip, TooltipContent, TooltipTrigger } from '@finn-auto/design-system/atoms/tooltip';
import { SERIES_PRIMARY, formatCompact, formatDay } from './chartTokens';
import { BOARD_TABS, describeFeature, summariseBoardFeatures } from './boardFeatures';
import type { BoardUsage, BoardUsageRow } from '../../services/usageApi';

export function BoardUsageSection({ usage }: { usage: BoardUsage }) {
  const max = usage.recruiters.reduce((acc, row) => Math.max(acc, row.total), 0) || 1;
  const untouched = usage.recruiters.filter((row) => row.total === 0);

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-zinc-900">Who used the boards</h3>
        <p className="mt-0.5 max-w-[80ch] text-xs text-zinc-500">
          A board belongs to one recruiter, so this is per board and per person — the only named
          figures on this page, and only for the boards. Every board owner has a row, and nobody
          else does. Open one for what that board has been used for and, more usefully, what it
          has not.
        </p>
      </div>

      <Accordion type="multiple" className="space-y-1.5">
        {usage.recruiters.map((row) => (
          <AccordionItem
            key={row.email}
            value={row.email}
            className="overflow-hidden rounded-lg border border-zinc-100 bg-zinc-50/60"
          >
            <AccordionTrigger className="px-3 py-2 hover:no-underline">
              <RecruiterHeader row={row} max={max} days={usage.range.days} />
            </AccordionTrigger>
            <AccordionContent
              alwaysMounted={false}
              className="border-t border-solid border-zinc-100 bg-white px-3 pt-3 pb-3"
            >
              <FeatureChecklist row={row} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* A truncated read returns an arbitrary subset, so a zero might be a document that was not
          read rather than a board that was not opened. Withhold the conclusion instead of stating
          one that would send support to the wrong person. */}
      {usage.truncated ? (
        <p className="text-xs text-zinc-500">
          This range hit the query ceiling, so every figure here is a floor and an empty board may
          simply not have been read. Narrow the range before reading anything into a zero.
        </p>
      ) : (
        untouched.length > 0 && (
          <p className="text-xs text-zinc-500">
            {untouched.length === 1
              ? `${untouched[0].name}'s board recorded nothing in this range.`
              : `${untouched.length} boards recorded nothing in this range: ${untouched
                  .map((row) => row.name)
                  .join(', ')}.`}{' '}
            Worth a conversation before it is worth a conclusion — somebody on leave looks exactly
            like somebody who has stopped using the tool.
          </p>
        )
      )}
    </section>
  );
}

function RecruiterHeader({
  row,
  max,
  days,
}: {
  row: BoardUsageRow;
  max: number;
  days: number;
}) {
  return (
    <div className="flex flex-1 flex-col gap-1.5 pr-3 text-left">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="text-xs font-semibold text-zinc-900">{row.name}</span>
        <span
          className="text-xs font-normal text-zinc-500"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {row.total === 0 ? (
            <span className="text-zinc-400">No activity in the last {days} days</span>
          ) : (
            <>
              {formatCompact(row.total)} events · {row.activeDays}{' '}
              {row.activeDays === 1 ? 'day' : 'days'} · last {formatDay(row.lastActive!)}
            </>
          )}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
        <div
          className="h-full rounded-full"
          style={{
            width: `${row.total > 0 ? Math.max((row.total / max) * 100, 1) : 0}%`,
            backgroundColor: SERIES_PRIMARY,
          }}
        />
      </div>
      {/* Tabs as chips rather than a table of columns: four numbers in a row are read as a shape,
          and an untouched tab should be visibly absent rather than a 0 among figures. */}
      <div className="flex flex-wrap gap-1">
        {BOARD_TABS.map((tab) => {
          const count = row.byTab[tab.tab] ?? 0;
          return (
            <span
              key={tab.tab}
              className={`rounded px-1.5 py-0.5 text-[11px] font-normal ${
                count > 0 ? 'bg-white text-zinc-700' : 'bg-transparent text-zinc-300'
              }`}
              style={count > 0 ? { fontVariantNumeric: 'tabular-nums' } : undefined}
            >
              {tab.label} {count > 0 ? formatCompact(count) : '—'}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Not used, then used — in that order on purpose.
 *
 * The support conversation starts from the gap, and putting the gap second buries it under a list
 * of things that are already going well.
 */
function FeatureChecklist({ row }: { row: BoardUsageRow }) {
  const tabs = summariseBoardFeatures(row.byTabEvent);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {tabs.map((tab) => (
        <div key={tab.tab}>
          <p className="mb-1.5 text-xs font-semibold text-zinc-900">{tab.label}</p>
          {tab.unused.length > 0 && (
            <ul className="mb-2 space-y-1">
              {tab.unused.map((label) => (
                <li key={label} className="flex items-baseline gap-1.5 text-xs text-zinc-500">
                  <span aria-hidden className="text-zinc-300">
                    ○
                  </span>
                  <FeatureLabel label={label} />
                </li>
              ))}
            </ul>
          )}
          {tab.used.length > 0 ? (
            <ul className="space-y-1">
              {tab.used.map((feature) => (
                <li
                  key={feature.label}
                  className="flex items-baseline justify-between gap-2 text-xs text-zinc-700"
                >
                  <span className="flex items-baseline gap-1.5">
                    <span aria-hidden className="text-zinc-400">
                      ●
                    </span>
                    <FeatureLabel label={feature.label} />
                  </span>
                  <span
                    className="shrink-0 text-zinc-500"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatCompact(feature.count)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-zinc-400">Nothing on this tab yet.</p>
          )}
        </div>
      ))}
    </div>
  );
}

/** The feature's own sentence on hover — the same one the page tree shows for its event. */
function FeatureLabel({ label }: { label: string }) {
  const description = describeFeature(label);
  if (!description) return <span>{label}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="underline decoration-dotted underline-offset-2">{label}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">{description}</TooltipContent>
    </Tooltip>
  );
}
